/**
 * MarkdownField — lightweight rich-text editor for analyst-authored report copy.
 *
 * A textarea with a formatting toolbar (bold / italic / heading / lists / link)
 * that inserts markdown around the current selection, plus a Preview tab that
 * renders through the same ReactMarkdown pipeline the report renderers use —
 * what the analyst previews here is what the customer report shows. Markdown
 * under the hood (no contenteditable, no new deps): the value is stored as
 * markdown inside authoredSections.
 */

import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Bold,
  Italic,
  Heading3,
  List,
  ListOrdered,
  Link2,
  Eye,
  PenLine,
} from 'lucide-react';

interface MarkdownFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
}

const PREVIEW_PROSE =
  '[&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 ' +
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_li]:my-0.5 ' +
  '[&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold ' +
  '[&_h1]:mt-3 [&_h2]:mt-3 [&_h3]:mt-3 [&_h1]:mb-1 [&_h2]:mb-1 [&_h3]:mb-1 ' +
  '[&_a]:underline [&_a]:underline-offset-2';

export function MarkdownField({
  value,
  onChange,
  placeholder,
  disabled,
  rows = 6,
}: MarkdownFieldProps) {
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Toolbar edits re-enter through the controlled value, so the selection has
  // to be restored after React commits — an effect keyed on value, not a RAF,
  // or the caret jumps to the end.
  const pendingSelection = useRef<[number, number] | null>(null);
  useEffect(() => {
    if (!pendingSelection.current) return;
    const [selStart, selEnd] = pendingSelection.current;
    pendingSelection.current = null;
    const el = textareaRef.current;
    if (el) {
      el.focus();
      el.setSelectionRange(selStart, selEnd);
    }
  }, [value]);

  function replaceRange(next: string, selStart: number, selEnd: number) {
    pendingSelection.current = [selStart, selEnd];
    onChange(next);
  }

  /** Wrap the selection (or a placeholder when collapsed) with markers. */
  function wrapSelection(before: string, after: string, fallback: string) {
    const el = textareaRef.current;
    if (!el || disabled) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const selected = value.slice(start, end) || fallback;
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    replaceRange(next, start + before.length, start + before.length + selected.length);
  }

  /** Prefix every line the selection touches (heading / lists). */
  function prefixLines(prefix: (lineIndex: number) => string) {
    const el = textareaRef.current;
    if (!el || disabled) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const blockStart = value.lastIndexOf('\n', start - 1) + 1;
    const newlineAfter = value.indexOf('\n', end);
    const blockEnd = newlineAfter === -1 ? value.length : newlineAfter;
    const prefixed = value
      .slice(blockStart, blockEnd)
      .split('\n')
      .map((line, i) => prefix(i) + line)
      .join('\n');
    const next = value.slice(0, blockStart) + prefixed + value.slice(blockEnd);
    replaceRange(next, blockStart, blockStart + prefixed.length);
  }

  const actions = [
    { icon: Bold, label: 'Bold', run: () => wrapSelection('**', '**', 'bold text') },
    { icon: Italic, label: 'Italic', run: () => wrapSelection('*', '*', 'italic text') },
    { icon: Heading3, label: 'Heading', run: () => prefixLines(() => '### ') },
    { icon: List, label: 'Bulleted list', run: () => prefixLines(() => '- ') },
    { icon: ListOrdered, label: 'Numbered list', run: () => prefixLines((i) => `${i + 1}. `) },
    { icon: Link2, label: 'Link', run: () => wrapSelection('[', '](https://)', 'link text') },
  ] as const;

  const tabButton = (key: 'write' | 'preview', label: string, Icon: typeof Eye) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded ${
        tab === key
          ? 'bg-bg-primary text-text-primary border border-border'
          : 'text-text-secondary hover:text-text-primary'
      }`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-bg-primary">
      <div className="flex items-center justify-between px-1.5 py-1 border-b border-border bg-bg-secondary">
        <div className="flex items-center gap-0.5">
          {actions.map((a) => (
            <button
              key={a.label}
              type="button"
              title={a.label}
              aria-label={a.label}
              disabled={disabled || tab === 'preview'}
              onMouseDown={(e) => e.preventDefault()} // don't steal the textarea selection
              onClick={a.run}
              className="p-1.5 rounded text-text-secondary hover:text-text-primary hover:bg-bg-tertiary disabled:opacity-40 disabled:pointer-events-none"
            >
              <a.icon className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-0.5">
          {tabButton('write', 'Write', PenLine)}
          {tabButton('preview', 'Preview', Eye)}
        </div>
      </div>
      {tab === 'write' ? (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          className="w-full px-3 py-2 text-sm bg-bg-primary text-text-primary resize-y focus:outline-none disabled:opacity-50 block"
        />
      ) : (
        <div className={`px-3 py-2 text-sm text-text-primary min-h-[6rem] ${PREVIEW_PROSE}`}>
          {value.trim() ? (
            <ReactMarkdown>{value}</ReactMarkdown>
          ) : (
            <p className="text-text-tertiary">Nothing to preview yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
